import React, { useState, useEffect, useCallback } from 'react';
import { collection, addDoc, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import './AdminGradingView.css';
import './AdminMoreDetailPages.css';
import { scopeForAdmin } from '../utils/schoolScope';
import { dataCacheService } from '../services/DataCacheService';

function AdminGradingView({ adminData }) {
  const [gradingScales, setGradingScales] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedScale, setSelectedScale] = useState(null);
  const [loading, setLoading] = useState(true);

  const [newScale, setNewScale] = useState({
    name: '',
    minScore: 90,
    maxScore: 100,
    grade: 'A',
    gpa: 4.0
  });

  const loadGradingScales = useCallback(async () => {
    console.log('🔥 AdminGrading: Loading grading scales');
    try {
      const ck = (p) => `${adminData?.schoolId || 'global'}:${p}`;
      const scalesData = scopeForAdmin(
        await dataCacheService.getDocs(ck('gradingScales'), collection(db, 'gradingScales')),
        adminData
      );
      console.log('🔥 AdminGrading: Grading scales loaded', scalesData.length, scalesData);
      setGradingScales(scalesData);
    } catch (error) {
      console.error('🔥 AdminGrading: Error loading grading scales:', error);
    } finally {
      setLoading(false);
    }
  }, [adminData]);

  useEffect(() => {
    loadGradingScales();
  }, [loadGradingScales]);

  const handleAddScale = async (e) => {
    e.preventDefault();
    try {
      await addDoc(collection(db, 'gradingScales'), {
        ...newScale,
        createdAt: new Date().toISOString(),
        schoolId: adminData?.schoolId || null
      });
      dataCacheService.invalidatePath('gradingScales');
      setShowAddForm(false);
      setNewScale({
        name: '',
        minScore: 90,
        maxScore: 100,
        grade: 'A',
        gpa: 4.0
      });
      loadGradingScales();
    } catch (error) {
      console.error('Error adding grading scale:', error);
      alert('Failed to add grading scale');
    }
  };

  const handleDeleteScale = async (scaleId) => {
    if (!confirm('Are you sure you want to delete this grading scale?')) return;
    try {
      await deleteDoc(doc(db, 'gradingScales', scaleId));
      dataCacheService.invalidatePath('gradingScales');
      loadGradingScales();
    } catch (error) {
      console.error('Error deleting grading scale:', error);
      alert('Failed to delete grading scale');
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // === Full-screen grading scale detail page ===
  if (selectedScale) {
    const s = selectedScale;
    console.log('🔥 AdminGrading: Showing detail for scale', s.id, s);
    return (
      <div className="admin-grading-view detail-page">
        <button className="detail-back-button" onClick={() => setSelectedScale(null)}>
          ◄ Back to Grading Scales
        </button>
        <div className="detail-card">
          <div className="detail-header">
            <div>
              <h2>{s.name || 'Grading Scale'}</h2>
              <p className="detail-subtitle">Grading scale band</p>
            </div>
            <span className="detail-status-badge" style={{ background: '#dcfce7', color: '#15803d', fontSize: '1.2rem', padding: '8px 22px' }}>
              {s.grade}
            </span>
          </div>

          <div className="detail-grid">
            <div className="detail-field">
              <span className="detail-label">Grade</span>
              <span className="detail-value">{s.grade || 'N/A'}</span>
            </div>
            <div className="detail-field">
              <span className="detail-label">Name</span>
              <span className="detail-value">{s.name || 'N/A'}</span>
            </div>
            <div className="detail-field">
              <span className="detail-label">Score Range</span>
              <span className="detail-value">{s.minScore}% – {s.maxScore}%</span>
            </div>
            <div className="detail-field">
              <span className="detail-label">GPA</span>
              <span className="detail-value">{s.gpa ?? 'N/A'}</span>
            </div>
            {s.createdAt && (
              <div className="detail-field">
                <span className="detail-label">Created</span>
                <span className="detail-value">
                  {s.createdAt.toDate ? s.createdAt.toDate().toLocaleDateString() : new Date(s.createdAt).toLocaleDateString()}
                </span>
              </div>
            )}
            {s.id && (
              <div className="detail-field">
                <span className="detail-label">Scale ID</span>
                <span className="detail-value" style={{ fontSize: '0.8rem', color: '#9ca3af' }}>{s.id}</span>
              </div>
            )}
          </div>

          <div className="detail-section">
            <h4>Range Visualization</h4>
            <div style={{ background: '#f3f4f6', borderRadius: '8px', height: '14px', overflow: 'hidden', position: 'relative' }}>
              <div style={{
                position: 'absolute',
                left: `${Math.max(0, s.minScore || 0)}%`,
                width: `${Math.min(100, (s.maxScore || 0) - (s.minScore || 0))}%`,
                top: 0, bottom: 0,
                background: '#22c55e',
                borderRadius: '8px'
              }} />
            </div>
            <p className="detail-empty-text" style={{ marginTop: '6px' }}>
              Students scoring {s.minScore}%–{s.maxScore}% receive a "{s.grade}" ({s.gpa} GPA)
            </p>
          </div>

          <div className="detail-actions">
            <button className="detail-btn-danger" onClick={async () => { await handleDeleteScale(s.id); setSelectedScale(null); }}>
              🗑️ Delete Scale
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-grading-view">
      <div className="view-header">
        <h2>Grading Scale</h2>
        <button className="add-button" onClick={() => setShowAddForm(true)}>
          + Add Scale
        </button>
      </div>

      <p className="count-text">Grading Scales: {gradingScales.length}</p>

      {showAddForm && (
        <div className="add-scale-form">
          <h3>Create New Grading Scale</h3>
          <form onSubmit={handleAddScale}>
            <div className="form-row">
              <div className="form-group">
                <label>Grade *</label>
                <input
                  type="text"
                  value={newScale.grade}
                  onChange={(e) => setNewScale({ ...newScale, grade: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={newScale.name}
                  onChange={(e) => setNewScale({ ...newScale, name: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Min Score *</label>
                <input
                  type="number"
                  value={newScale.minScore}
                  onChange={(e) => setNewScale({ ...newScale, minScore: parseFloat(e.target.value) })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Max Score *</label>
                <input
                  type="number"
                  value={newScale.maxScore}
                  onChange={(e) => setNewScale({ ...newScale, maxScore: parseFloat(e.target.value) })}
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label>GPA *</label>
              <input
                type="number"
                step="0.1"
                value={newScale.gpa}
                onChange={(e) => setNewScale({ ...newScale, gpa: parseFloat(e.target.value) })}
                required
              />
            </div>
            <div className="form-actions">
              <button type="button" onClick={() => setShowAddForm(false)}>
                Cancel
              </button>
              <button type="submit">Create Scale</button>
            </div>
          </form>
        </div>
      )}

      {gradingScales.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🎓</div>
          <h3>No Grading Scales</h3>
          <p>Create your first grading scale to get started</p>
        </div>
      ) : (
        <div className="scales-list">
          {gradingScales.map(scale => (
            <div key={scale.id} className="scale-card" style={{ cursor: 'pointer' }} onClick={() => setSelectedScale(scale)}>
              <div className="scale-grade">
                <span className="grade-letter">{scale.grade}</span>
              </div>
              <div className="scale-info">
                <h4>{scale.name}</h4>
                <p>Range: {scale.minScore}% - {scale.maxScore}%</p>
                <p>GPA: {scale.gpa}</p>
              </div>
              <button
                className="delete-button"
                onClick={(e) => { e.stopPropagation(); handleDeleteScale(scale.id); }}
              >
                🗑️
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default AdminGradingView;
